import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { identityApi, IdentityError } from '../../lib/identityApi';

// 客户凭一次性链接设置密码。
// 明文密码只在本页提交给后端换取 Argon2id 哈希，不落库、不入日志、不进 URL。

const MESSAGES: Record<string, string> = {
  token_invalid_or_used: '该链接已被使用或无效。请联系管理员重新发送。',
  token_expired: '该链接已过期。请联系管理员重新发送。',
  password_too_short: '密码至少需要 12 个字符',
  password_too_long: '密码过长',
  password_too_common: '该密码过于简单，请更换',
  password_required: '请输入密码',
};

export function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [status, setStatus] = useState<'checking' | 'ready' | 'unusable'>('checking');
  const [reason, setReason] = useState<string>('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setStatus('unusable'); setReason('token_invalid_or_used'); return; }
    identityApi.tokenStatus(token)
      .then((r) => {
        if (r.usable) setStatus('ready');
        else { setStatus('unusable'); setReason(r.reason || 'token_invalid_or_used'); }
      })
      .catch(() => { setStatus('unusable'); setReason('token_invalid_or_used'); });
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError('两次输入的密码不一致'); return; }
    setBusy(true);
    try {
      await identityApi.setPassword(token, password);
      // 密码不保留在内存状态中，避免在组件里长时间驻留。
      setPassword(''); setConfirm('');
      setDone(true);
    } catch (err) {
      setError(err instanceof IdentityError ? (MESSAGES[err.message] || '设置失败') : '设置失败');
      // 令牌可能已被使用：重新检查状态，给出准确提示。
      if (err instanceof IdentityError && err.message.startsWith('token_')) {
        setStatus('unusable');
        setReason(err.message);
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="login-wrap">
      <div className="login-pane">
        <form className="login-card" onSubmit={submit}>
          <div className="login-brand">
            <div className="brand-mark logo"><img src="/logo.png" alt="Pinnacle Laser" /></div>
            <div className="brand-text">
              <b>设置密码</b>
              <span>Pinnacle Laser · 统一身份</span>
            </div>
          </div>

          <div className="login-form">
            {status === 'checking' && <div className="notice info">正在校验链接…</div>}

            {status === 'unusable' && (
              <div className="notice bad" role="alert">{MESSAGES[reason] || '链接不可用'}</div>
            )}

            {status === 'ready' && !done && (
              <>
                <label className="field">
                  <label>新密码</label>
                  <input className="txt" data-testid="new-password" type="password" autoComplete="new-password"
                    value={password} onChange={e => setPassword(e.target.value)} />
                </label>
                <label className="field">
                  <label>确认密码</label>
                  <input className="txt" data-testid="confirm-password" type="password" autoComplete="new-password"
                    value={confirm} onChange={e => setConfirm(e.target.value)} />
                </label>
                {error && <div className="notice bad" role="alert">{error}</div>}
                <button className="btn primary" type="submit" data-testid="submit-password"
                  disabled={busy || !password}
                  style={{ justifyContent: 'center', padding: '10px 12px' }}>
                  {busy ? '提交中…' : '设置密码'}
                </button>
              </>
            )}

            {done && (
              <div className="notice info">
                密码已设置成功，请回到 Portal 使用新密码登录。
              </div>
            )}
          </div>

          <div className="login-foot">
            至少 12 个字符。该链接使用一次后立即失效。
          </div>
        </form>
      </div>
    </div>
  );
}